import React from "react"
import { useQuery } from "@apollo/client"
import { useParams } from "react-router-dom"
import { gql } from "~/__generated__"
import { useViewerMaybe } from "~/auth/use-viewer"
import { GraphqlError } from "~/ui/errors"
import Text from "~/ui/typography"
import { useDocumentTitle } from "~/common/use-document-title"
import { ScreenLoading } from "~/ui/screen-loading"
import { LettermemeFooter } from "./lettermeme-footer"
import { NewsletterView } from "./newsletter-view"
import { useTrackingCode } from "~/common/use-tracking-code"

const lettermemeArchiveQuery = gql(/* GraphQL */ `
  query lettermemeArchive($urlPath: String!, $id: ID!) {
    lettermeme(urlPath: $urlPath) {
      ...LettermemeView
    }
    node(id: $id) {
      ... on NewsletterSummary {
        __typename
        ...NewsletterView
      }
    }
  }
`)

const viewerOwnedLettermemesQuery = gql(/* GraphQL */ `
  query ViewerOwnedLettermemesForLettermemeArchiveScreen {
    viewer {
      id
      ownedLettermemes {
        id
      }
    }
  }
`)

export const LettermemeArchiveScreen: React.FC = () => {
  const { urlPath, id } = useParams<{ urlPath: string; id: string }>()
  const { viewer } = useViewerMaybe()
  const { data, error, loading } = useQuery(lettermemeArchiveQuery, {
    variables: { urlPath: urlPath!, id: id! },
    skip: !urlPath || !id,
  })
  const { data: ownedData } = useQuery(viewerOwnedLettermemesQuery, {
    fetchPolicy: "cache-first",
    skip: !viewer,
  })

  useDocumentTitle(data?.lettermeme?.name || "Lettermeme")
  useTrackingCode(data?.lettermeme?.trackingCode)
  if (error) return <GraphqlError error={error} />
  if (loading) return <ScreenLoading />

  if (!data?.lettermeme) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Lettermeme not found
        </Text>
        <Text>The lettermeme you're looking for doesn't exist.</Text>
      </div>
    )
  }

  const lettermeme = data.lettermeme
  const newsletter = data.node
  const isOwner = Boolean(
    viewer &&
      lettermeme.id &&
      ownedData?.viewer?.ownedLettermemes?.some(
        (ownedLettermeme) => ownedLettermeme.id === lettermeme.id
      )
  )
  const isPaidLocked =
    lettermeme.monetizationEnabled && lettermeme.viewerIsSubscribed !== true && !isOwner
  const isFreeLocked = !viewer && !lettermeme.monetizationEnabled
  const isPreview = isPaidLocked || isFreeLocked

  if (!newsletter || newsletter.__typename !== "NewsletterSummary") {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Text as="h1" className="mb-4 text-2xl font-bold">
          Newsletter not found
        </Text>
        <Text>The newsletter you're looking for doesn't exist.</Text>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl p-8">
      <NewsletterView
        lettermeme={lettermeme}
        newsletter={newsletter}
        variant={isPreview ? "preview" : "full"}
      />
      <LettermemeFooter />
    </div>
  )
}
